import type { BankTransferOption } from '@repo/api-contracts';
import { type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { useBusinessProfileQuery, useCheckBusinessNumber, useMeQuery } from '@/entities/user';
import { Button, cn, FormField } from '@/shared/ui';
import {
  type BankTransferFormErrors,
  type BankTransferFormValues,
  parseBankTransferForm,
} from '../model/bank-transfer-form';
import { bankTransferApiErrorMessage } from '../model/bank-transfer-message';
import { usePlaceBankTransfer } from '../model/use-place-bank-transfer';
import { TaxInvoiceFields } from './tax-invoice-fields';

export const BankTransferPanel = ({
  orderId,
  option,
  onPlaced,
}: {
  readonly orderId: string;
  readonly option: BankTransferOption;
  readonly onPlaced: (orderId: string) => void;
}): ReactElement => {
  const profileQuery = useBusinessProfileQuery();
  const me = useMeQuery();
  const initialProfile = option.profile;
  const [values, setValues] = useState<BankTransferFormValues>(() => ({
    orderId,
    depositorName: option.defaultDepositorName,
    requestTaxInvoice: true,
    saveToProfile: true,
    businessNumber: initialProfile?.businessNumber ?? '',
    companyName: initialProfile?.companyName ?? '',
    representativeName: initialProfile?.representativeName ?? '',
    address: initialProfile?.address ?? '',
    businessType: initialProfile?.businessType ?? '',
    businessItem: initialProfile?.businessItem ?? '',
    invoiceEmail: initialProfile?.invoiceEmail ?? me.data?.email ?? '',
    contactName: initialProfile?.contactName ?? '',
  }));
  const edited = useRef(new Set<keyof BankTransferFormValues>());
  const [errors, setErrors] = useState<BankTransferFormErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const submitting = useRef(false);
  const placing = usePlaceBankTransfer();
  const business = useCheckBusinessNumber(values.businessNumber);
  const radioName = useId();
  useEffect(() => {
    const profile = option.profile ?? profileQuery.data;
    setValues((previous) => {
      const defaults = {
        businessNumber: profile?.businessNumber ?? '',
        companyName: profile?.companyName ?? '',
        representativeName: profile?.representativeName ?? '',
        address: profile?.address ?? '',
        businessType: profile?.businessType ?? '',
        businessItem: profile?.businessItem ?? '',
        invoiceEmail: profile?.invoiceEmail ?? me.data?.email ?? '',
        contactName: profile?.contactName ?? '',
      };
      const next = { ...previous };
      for (const key of Object.keys(defaults) as (keyof typeof defaults)[]) {
        if (!edited.current.has(key)) next[key] = defaults[key];
      }
      return next;
    });
  }, [option.profile, profileQuery.data, me.data?.email]);
  const change = <K extends keyof BankTransferFormValues>(
    key: K,
    value: BankTransferFormValues[K],
  ) => {
    edited.current.add(key);
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: undefined }));
  };
  const blocked = values.requestTaxInvoice && (business.state === 'CLOSED' || business.isPending);
  const submit = async (): Promise<void> => {
    if (submitting.current || placing.isPending || blocked) return;
    const parsed = parseBankTransferForm(values);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    submitting.current = true;
    setErrors({});
    setFailure(null);
    try {
      await placing.place(parsed.input);
      onPlaced(orderId);
    } catch (error) {
      setFailure(bankTransferApiErrorMessage(error));
    } finally {
      submitting.current = false;
    }
  };
  return (
    <form
      noValidate
      className="flex flex-col gap-8 border-t border-foreground/10 pt-8"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="flex items-center justify-between border-b border-foreground/10 pb-5">
        <h2 className="text-lg font-semibold">계좌이체 주문</h2>
        <p className="text-lg font-semibold">{option.amount.toLocaleString('ko-KR')}원</p>
      </div>
      <fieldset disabled={placing.isPending} className="flex min-w-0 flex-col gap-6">
        <FormField
          label="입금자명"
          isRequired
          required
          className="h-11"
          maxLength={50}
          value={values.depositorName}
          error={errors.depositorName}
          onChange={(event) => change('depositorName', event.target.value)}
        />
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-3 text-sm font-medium">세금계산서</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                { value: true, label: '발행 요청' },
                { value: false, label: '필요 없음' },
              ] as const
            ).map((item) => (
              <label
                key={item.label}
                className={cn(
                  'flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm',
                  values.requestTaxInvoice === item.value
                    ? 'border-foreground'
                    : 'border-input hover:border-foreground/40',
                )}
              >
                <input
                  type="radio"
                  name={radioName}
                  checked={values.requestTaxInvoice === item.value}
                  onChange={() => change('requestTaxInvoice', item.value)}
                  className="size-4 accent-primary"
                />
                {item.label}
              </label>
            ))}
          </div>
        </fieldset>
        {values.requestTaxInvoice ? (
          <TaxInvoiceFields
            values={values}
            errors={errors}
            onChange={change}
            onCheck={() => {
              void business.check();
            }}
            businessState={business.state}
            isChecking={business.isPending}
          />
        ) : null}
      </fieldset>
      {errors.orderId ? (
        <p role="alert" className="text-sm text-destructive">
          {errors.orderId}
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      ) : null}
      <Button
        type="submit"
        className="h-12 w-full sm:w-auto sm:self-end"
        disabled={placing.isPending || blocked}
      >
        {placing.isPending ? '주문 처리 중…' : '계좌이체로 주문'}
      </Button>
    </form>
  );
};
